import { NgModule, CUSTOM_ELEMENTS_SCHEMA, provideAppInitializer, inject } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AuthService } from './shared/services/auth.service';
import { AuthGuard } from './shared/services/auth.guard';

// Web Components
import '@maersk-global/mds-components-core/mc-tab-bar';
import '@maersk-global/mds-components-core/mc-tab';
import '@maersk-global/mds-components-core/mc-pagination';
import '@maersk-global/mds-components-core/mc-tag';
import '@maersk-global/mds-components-core/mc-icon';
import '@maersk-global/mds-components-core/mc-input';
import '@maersk-global/mds-components-core/mc-button';
import '@maersk-global/mds-components-core/mc-checkbox';
import '@maersk-global/mds-components-core/mc-select';
import '@maersk-global/mds-components-core/mc-option';
import '@maersk-global/mds-components-core/mc-modal';
import '@maersk-global/mds-components-core/mc-loading-indicator';
import '@maersk-global/mds-components-core/mc-typeahead';
import '@maersk-global/mds-components-core/mc-multi-select';
import '@maersk-global/mds-components-core/mc-file-upload';
import '@maersk-global/mds-components-core-select-native';
import '@maersk-global/mds-components-core-input-date';
import '@maersk-global/mds-components-core/mc-toast';
import '@maersk-global/mds-components-core/mc-notification';
import '@maersk-global/mds-components-core/mc-popover';

import { NgxSpinnerModule } from 'ngx-spinner';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';

import { environment } from 'src/environments/environment';

import {
  IPublicClientApplication,
  PublicClientApplication,
  InteractionType,
  BrowserCacheLocation,
  LogLevel
} from '@azure/msal-browser';
import {
  MsalGuard,
  MsalInterceptor,
  MsalBroadcastService,
  MsalInterceptorConfiguration,
  MsalModule,
  MsalService,
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalGuardConfiguration,
  MsalRedirectComponent
} from '@azure/msal-angular';

import { FailedComponent } from './failed/failed.component';
import { InvalidFileComponent } from './invalid-file/invalid-file.component';

export function loggerCallback(logLevel: LogLevel, message: string) {
  // console.log(message);
}

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.msalConfig.auth.clientId,
      authority: environment.msalConfig.auth.authority,
      redirectUri: window.location.origin + '/reliability/oil-analysis/',
      postLogoutRedirectUri: window.location.origin + '/reliability/oil-analysis/'
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage
    },
    system: {
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false
      }
    }
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();

  // Format base URL to clean trailing slashes
  const baseUrl = environment.apiBaseUrl.replace(/\/+$/, '');

  // Protect exact base, wildcard subpaths, and explicit ValidateToken endpoint
  protectedResourceMap.set(`${baseUrl}/*`, [environment.apiConfig.uri]);
  protectedResourceMap.set(baseUrl, [environment.apiConfig.uri]);
  if (environment.apiConfig?.uri) {
    protectedResourceMap.set(environment.apiConfig.uri, [environment.apiConfig.uri]);
  }

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [environment.apiConfig.uri]
    },
    loginFailedRoute: '/login-failed'
  };
}

@NgModule({
  declarations: [AppComponent, FailedComponent, InvalidFileComponent],
  imports: [BrowserModule, AppRoutingModule, FormsModule, ReactiveFormsModule, NgxSpinnerModule, MsalModule],
  providers: [
    provideHttpClient(withInterceptorsFromDi()),
    provideAppInitializer(async () => {
      const msal = inject(MsalService);

      // 1. Initialize MSAL instance
      await msal.instance.initialize();

      // 2. Resolve any pending redirect login promises
      await msal.instance.handleRedirectPromise();

      // 3. Set the active account if not already set
      const accounts = msal.instance.getAllAccounts();
      if (!msal.instance.getActiveAccount() && accounts.length > 0) {
        msal.instance.setActiveAccount(accounts[0]);
      }
    }),
    AuthService,
    AuthGuard,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService
  ],
  bootstrap: [AppComponent, MsalRedirectComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AppModule {}
